import { useRef } from 'react';
import { useRoute } from '../../context/RouteContext';
import { formatDistance, formatDuration } from '../../utils/formatters';
import { CurvinessBadge } from '../common/CurvinessBadge';
import { ElevationGraph } from '../common/ElevationGraph';
import { RoadBreakdown } from '../common/RoadBreakdown';
import './RouteInfo.css';

function RulerIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/>
      <path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/>
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

export function RouteInfo() {
  const { state } = useRoute();
  const { current, isRouting, routeError, elevation } = state;
  const { distance, duration, waypoints, geometry } = current;

  const hasRoute  = waypoints.length >= 2;
  const hasStats  = hasRoute && distance != null && geometry != null;

  // A recompute (toggling an avoid option, moving a waypoint, …) nulls
  // geometry/distance/elevation for a moment. Cache the last fully-computed
  // route + elevation so we keep rendering those values — at full size — and
  // just overlay a loading state, instead of collapsing the block and snapping
  // it back when the new numbers land.
  const lastGoodRef = useRef(null);
  const lastElevRef = useRef(null);

  if (hasStats) {
    lastGoodRef.current = { distance, duration, geometry, waypointCount: waypoints.length };
  }
  if (elevation) lastElevRef.current = elevation;
  if (!hasRoute) { lastGoodRef.current = null; lastElevRef.current = null; }

  const good    = hasStats
    ? { distance, duration, geometry, waypointCount: waypoints.length }
    : lastGoodRef.current;
  const elev    = elevation || lastElevRef.current;
  const loading = isRouting || (hasRoute && !hasStats && !routeError);

  // Nothing to show when idle — avoids an empty block between sidebar dividers.
  if (!hasRoute && !isRouting && !routeError) return null;

  return (
    <div className={`route-info${loading ? ' route-info--loading' : ''}`}>
      {loading && <div className="route-info__progress" aria-hidden="true" />}

      {routeError && !isRouting && (
        <div className="route-info__error">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {routeError}
        </div>
      )}

      {good ? (
        <div className="route-info__content">
          <div className="route-info__curviness-row">
            <span className="route-info__curviness-label">Curviness</span>
            <CurvinessBadge geometry={good.geometry} variant="chip" />
          </div>

          <RoadBreakdown geometry={good.geometry} />

          {elev && <ElevationGraph elevation={elev} />}

          <div className="route-info__stats">
            <div className="route-info__stat">
              <RulerIcon />
              <div>
                <div className="route-info__stat-value">{formatDistance(good.distance)}</div>
                <div className="route-info__stat-label">Distance</div>
              </div>
            </div>
            <div className="route-info__divider" />
            <div className="route-info__stat">
              <ClockIcon />
              <div>
                <div className="route-info__stat-value">{formatDuration(good.duration)}</div>
                <div className="route-info__stat-label">Est. time</div>
              </div>
            </div>
            <div className="route-info__divider" />
            <div className="route-info__stat">
              <PinIcon />
              <div>
                <div className="route-info__stat-value">{good.waypointCount}</div>
                <div className="route-info__stat-label">Waypoints</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        !routeError && (
          // First-ever calc: no previous values to hold, so reserve the space
          // with a skeleton rather than popping in from zero height.
          <div className="route-info__skeleton" aria-label="Calculating route">
            <div className="route-info__sk-row" />
            <div className="route-info__sk-stats">
              <div className="route-info__sk-cell" />
              <div className="route-info__sk-cell" />
              <div className="route-info__sk-cell" />
            </div>
          </div>
        )
      )}
    </div>
  );
}
